// src/components/MyPurchasesPanel.jsx
// Visual base: prototype L8413-8521 (midnight). WP-3: source is now the live
// Firestore /pendingPurchases listener (usePendingPurchases) instead of the
// localStorage map. Maps the WP-2 schema (pending|approved|rejected, items[],
// totalAmount in minor units, paymentReference) onto the prototype's card design.
import React from 'react';
import { useNavigate } from 'react-router-dom';
import PaymentInstructionsBlock from './PaymentInstructionsBlock.jsx';
import { usePendingPurchases } from '../storage/usePendingPurchases.js';
import { AUTH_MODE } from '../auth/firebase.js';

function fmtTs(ts) {
    if (!ts) return '—';
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-GB',
        { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function hkd(minor) {
    if (typeof minor !== 'number') return '';
    return 'HK$' + (minor / 100).toFixed(minor % 100 === 0 ? 0 : 2);
}
function packsLabel(p) {
    if (Array.isArray(p.items) && p.items.length) return p.items.map(i => i.title || i.packId).join(', ');
    if (Array.isArray(p.packIds)) return p.packIds.join(', ');
    return 'Pack purchase';
}

const STATUS = {
    pending: { label: 'Awaiting Confirmation', cls: 'bg-amber-500/20 text-amber-300' },
    approved: { label: 'Confirmed', cls: 'bg-green-500/20 text-green-300' },
    rejected: { label: 'Rejected', cls: 'bg-red-500/20 text-red-300' }
};

const MyPurchasesPanel = ({ state, setActiveTab }) => {
    const navigate = useNavigate();
    const { pending, loading } = usePendingPurchases();

    const cartStillHasPacks = (state.cart || []).length > 0;
    const cartReminderBanner = cartStillHasPacks ? (
        <div className="border border-amber-500/40 bg-amber-500/10 p-4 mb-4 flex items-start justify-between gap-4">
            <div className="font-serif text-amber-100 text-sm">
                <span className="font-display text-amber-300 text-xs mr-2 font-semibold">Reminder:</span>
                You have <strong>{state.cart.length} pack{state.cart.length === 1 ? '' : 's'}</strong> still in your cart.
            </div>
            <button onClick={() => navigate('/cart')}
                className="shrink-0 px-3 py-1.5 border border-amber-400 text-amber-200 font-display text-sm hover:bg-amber-500/10 transition-colors font-medium rounded-full">
                View Cart →
            </button>
        </div>
    ) : null;

    if (loading) {
        return <div>{cartReminderBanner}<p className="font-serif italic text-midnight-muted py-8 text-center">Loading your purchases…</p></div>;
    }

    if (!pending.length) {
        return (
            <div>
                {cartReminderBanner}
                <div className="text-center py-12">
                    <p className="font-serif italic text-midnight-muted">No PayMe/FPS payment requests yet.</p>
                    <p className="font-serif text-midnight-muted text-sm mt-2">Card payments are handled separately by Stripe. Confirmed packs appear in your Library.</p>
                    <p className="font-serif text-midnight-muted text-sm mt-2">
                        {AUTH_MODE === 'mock'
                            ? 'Purchases load from Firestore (run with emulator/firebase mode to see them).'
                            : <>Confirmed packs appear in <a onClick={() => setActiveTab && setActiveTab('overview')} className="underline cursor-pointer text-midnight-accent">Overview</a> and your Library.</>}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {cartReminderBanner}
            <p className="font-serif text-midnight-muted text-sm">PayMe/FPS payment requests. Card payments are handled separately by Stripe; confirmed packs appear in your Library.</p>
            {pending.map(p => {
                const st = STATUS[p.status] || { label: p.status, cls: 'bg-midnight-muted/20 text-midnight-muted' };
                const refCode = p.paymentReference || p.id;
                const label = packsLabel(p);
                const firstPack = (p.packIds && p.packIds[0]) || (p.items && p.items[0] && p.items[0].packId);
                return (
                    <div key={p.id} className="border border-midnight-accent/30 bg-midnight-pageDark/40 p-5">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <div className="font-display text-midnight-accent text-xs mb-1 font-semibold">Reference {refCode}</div>
                                <div className="font-display text-midnight-text text-lg">{label}</div>
                                <div className="font-serif italic text-midnight-muted text-sm">{hkd(p.totalAmount)} · Submitted {fmtTs(p.submittedAt)}</div>
                            </div>
                            <span className={`px-3 py-1.5 font-display text-sm whitespace-nowrap font-medium rounded-full ${st.cls}`}>{st.label}</span>
                        </div>

                        {p.productType === 'pack_offer' && <p className="text-sm mb-3">{p.offer === 'taster' ? p.durationDays + '-day Taster; access starts after approval.' : '12-month upgrade with Taster credit applied.'}</p>}
                        {p.status === 'pending' && p.productType === 'pack_offer' && <p className="text-sm">Payment submitted for review. Do not transfer again. We normally confirm within 8–12 hours.</p>}
                        {p.status === 'pending' && p.productType !== 'pack_offer' && (
                            <div className="border-t border-midnight-accent/20 pt-4">
                                <div className="font-serif text-midnight-text text-sm mb-3">
                                    Pay <span className="font-display text-midnight-accent">{hkd(p.totalAmount)}</span> using one of the methods below, quoting your reference.
                                </div>
                                <PaymentInstructionsBlock
                                    booking={state.bookingContent}
                                    referenceCode={refCode}
                                    prefilledMessage={`Payment for ${label} (${hkd(p.totalAmount)}), reference ${refCode}`}
                                    theme="midnight"
                                />
                                <p className="font-serif italic text-midnight-muted text-xs mt-3">
                                    Already paid? Danielle will confirm within 8–12 hours.
                                </p>
                            </div>
                        )}

                        {p.status === 'approved' && (
                            <div className="border-t border-midnight-accent/20 pt-4">
                                <p className="font-serif text-green-300 text-sm">✓ Payment confirmed{p.reviewedAt ? ` on ${fmtTs(p.reviewedAt)}` : ''}.</p>
                                <button onClick={() => navigate('/dashboard')}
                                    className="mt-3 px-4 py-2 border border-midnight-accent/50 text-midnight-accent font-display text-sm hover:bg-midnight-accent/10 transition-colors font-semibold">
                                    Open Pack →
                                </button>
                            </div>
                        )}

                        {p.status === 'rejected' && (
                            <div className="border-t border-midnight-accent/20 pt-4">
                                <p className="font-serif text-red-300 text-sm">Payment not confirmed.</p>
                                <p className="font-serif italic text-midnight-text text-sm mt-1">Reason: {p.reviewNotes || '—'}</p>
                                {firstPack && (
                                    <button onClick={() => navigate('/pack-payment-choice/' + firstPack)}
                                        className="mt-3 px-4 py-2 border border-midnight-accent/50 text-midnight-accent font-display text-sm hover:bg-midnight-accent/10 transition-colors font-semibold">
                                        Try again
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default MyPurchasesPanel;
